import { useState } from 'react';
import { Dialog, DialogTitle, DialogPanel, DialogBackdrop } from '@headlessui/react';
import { ExclamationTriangleIcon, KeyIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import api from '../utils/api';
import store from '../utils/store';

export default function RotateApiKeyModal({ open, setOpen, email, onRotated }) {
  const [password, setPassword] = useState('');
  const [isRotating, setIsRotating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [newToken, setNewToken] = useState('');
  const [copyButtonText, setCopyButtonText] = useState('Copy');

  const handleClose = () => {
    setOpen(false);
    setPassword('');
    setErrorMsg('');
    setNewToken('');
    setCopyButtonText('Copy');
  };

  const handleRotate = async (e) => {
    e.preventDefault();
    if (!password) {
      setErrorMsg('Please enter your password.');
      return;
    }

    setErrorMsg('');
    setIsRotating(true);

    try {
      // Password must be verified against the login endpoint before
      // the rotation request is authorized.
      await api.verifyPassword(email, password);
      const accessToken = store.getItem('accessToken');
      const response = await api.rotateApiKey(accessToken);
      setNewToken(response.token);
      if (onRotated) {
        onRotated(response.token);
      }
    } catch (error) {
      setErrorMsg(error.message || 'Failed to rotate API key.');
    } finally {
      setIsRotating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newToken);
    setCopyButtonText('Copied!');
    setTimeout(() => setCopyButtonText('Copy'), 2000);
  };

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-10">
      <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:w-full sm:max-w-lg">
            {newToken ? (
              <>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                      <KeyIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1 min-w-0">
                      <DialogTitle as="h3" className="text-lg leading-6 font-medium text-gray-900">
                        API Key Rotated
                      </DialogTitle>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Your API key has been rotated successfully. Your old key no longer works —
                          update any integrations to use the new key below.
                        </p>
                        <div className="mt-4 flex items-center gap-x-2">
                          <code className="block min-w-0 flex-1 overflow-x-auto rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-900">
                            {newToken}
                          </code>
                          <button
                            type="button"
                            onClick={handleCopy}
                            className="inline-flex flex-shrink-0 items-center gap-x-1 rounded-md bg-white px-2.5 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                            {copyButtonText}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-mypurple px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-mypurple/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mypurple sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Done
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={handleRotate}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                      <DialogTitle as="h3" className="text-lg leading-6 font-medium text-gray-900">
                        Rotate API Key
                      </DialogTitle>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Rotating your API key immediately invalidates your current key. Any
                          integrations using it will stop working until you update them with the
                          new key. Please enter your password to confirm this action.
                        </p>
                        <label htmlFor="rotate-password" className="sr-only">
                          Your password
                        </label>
                        <input
                          id="rotate-password"
                          name="password"
                          type="password"
                          autoComplete="current-password"
                          placeholder="Your password"
                          className="mt-4 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-600 focus:ring-red-600 sm:text-sm"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            setErrorMsg('');
                          }}
                        />
                        {errorMsg && (
                          <p className="mt-2 text-sm text-red-600">{errorMsg}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isRotating}
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {isRotating ? 'Rotating...' : 'Yes, rotate my API key'}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isRotating}
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
